import { z } from "zod";

/**
 * What a `worker` agent runs. The harness enforces one contract: a process
 * that listens on the unix socket it is handed. How that process comes to
 * exist is one of two modes, resolved at deploy time and stored on the
 * profile (`profiles.workload`) so every bundle records it:
 *
 * - `wrap`: the bundle has a module with a default export `{ fetch(request,
 *   env, ctx) }`. The harness owns the server. Zero-config, and the
 *   Cloudflare-compatible path.
 * - `run`: the bundle names a command. The harness spawns it with
 *   `FORGEDOCK_LISTEN` set to the socket path and waits for the socket to
 *   appear. Any framework, any binary in the jail.
 */

/** The module a `wrap` worker exports from when `entry` is not set. */
export const DEFAULT_WORKER_ENTRY = "worker.ts";

const entryPathSchema = z
	.string()
	.trim()
	.min(1)
	.max(512)
	.refine((value) => !value.startsWith("/") && !value.includes("\\"), {
		message: "Entry must be a relative path inside the bundle",
	})
	.refine((value) => !value.split("/").some((segment) => segment === ".."), {
		message: "Entry may not traverse outside the bundle",
	});

export const workerWorkloadSchema = z.discriminatedUnion("mode", [
	z.object({ mode: z.literal("wrap"), entry: entryPathSchema }).strict(),
	z.object({ mode: z.literal("run"), command: z.array(z.string().min(1)).min(1).max(64) }).strict(),
]);

export type WorkerWorkload = z.infer<typeof workerWorkloadSchema>;

export function defaultWorkerWorkload(): WorkerWorkload {
	return { mode: "wrap", entry: DEFAULT_WORKER_ENTRY };
}

/** A stored `workload` column (jsonb, nullable) as a workload, or null. */
export function parseStoredWorkload(value: unknown): WorkerWorkload | null {
	const parsed = workerWorkloadSchema.safeParse(value);
	return parsed.success ? parsed.data : null;
}

/** One line for a card or a summary: `worker.ts (wrapped)` or `run: bun src/server.ts`. */
export function describeWorkerWorkload(workload: WorkerWorkload | null): string {
	const resolved = workload ?? defaultWorkerWorkload();
	if (resolved.mode === "wrap") return `${resolved.entry} (wrapped)`;
	return `run: ${resolved.command.join(" ")}`;
}
